/**
 * @file DeliveryQuoteReasonAlert.tsx
 * @description Почему расчёт не выполнен + что с этим делать (phase19.12 шаг 5).
 *
 * Тексты и действия берутся из реестра `QUOTE_REASON_META`: новая причина от
 * сервера = запись в реестре, а не ветка в компоненте.
 */

import type { ReactNode } from "react";
import { Link } from "react-router";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { resolveQuoteReason } from "../../lib/delivery-quote-meta";

interface Props {
  reason: string | undefined;
  /** Ссылка на настройки перевозчика в текущем магазине. */
  carrierSettingsHref?: string | null;
  /** Раздел «Интеграции» доступен только платформенному администратору. */
  integrationsHref?: string | null;
  onRetry?: () => void;
}

/** Фокус на проблемном поле формы — поля живут в соседнем компоненте. */
function focusField(field: "city" | "weight"): void {
  const id = field === "city" ? "delivery-quote-city" : "delivery-quote-weight-0";
  const el = document.getElementById(id);
  if (el instanceof HTMLInputElement) {
    el.focus();
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }
}

export function DeliveryQuoteReasonAlert({
  reason,
  carrierSettingsHref,
  integrationsHref,
  onRetry,
}: Props) {
  const meta = resolveQuoteReason(reason);
  const { action } = meta;

  const renderAction = (): ReactNode => {
    if (!action || !meta.actionLabel) return null;
    if (action.kind === "carrier-settings") {
      if (!carrierSettingsHref) return null;
      return (
        <Button variant="outline" size="sm" asChild>
          <Link to={carrierSettingsHref}>{meta.actionLabel}</Link>
        </Button>
      );
    }
    if (action.kind === "integrations") {
      if (!integrationsHref) return null;
      return (
        <Button variant="outline" size="sm" asChild>
          <Link to={integrationsHref}>{meta.actionLabel}</Link>
        </Button>
      );
    }
    if (action.kind === "retry") {
      if (!onRetry) return null;
      return (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {meta.actionLabel}
        </Button>
      );
    }
    return (
      <Button variant="outline" size="sm" onClick={() => focusField(action.field)}>
        {meta.actionLabel}
      </Button>
    );
  };

  return (
    <Alert severity={meta.severity} title={meta.title}>
      <div className="flex flex-col items-start gap-2">
        <span>{meta.text}</span>
        {renderAction()}
      </div>
    </Alert>
  );
}
